import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';

import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Box from '@mui/material/Box';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useMapStore } from '@shared/hooks/useMapStore';
import { useBasket } from '@features/basket/hooks/useBasket';
import {
  resolveEffectivePeriod,
  resolveEffectivePrice,
  hasValidPricing,
  usePeriodStore,
  currencySymbol,
} from '@shared/pricing';
import type { RouteProduct } from '../types';
import {
  productTypeColor,
  productTypeLabel,
  holdingDotColor,
  holdingLabel,
} from '@shared/constants/product';
import { coverageLabel } from '@shared/constants/coverage';

interface Props {
  products: RouteProduct[];
  loading?: boolean;
}

export default function RouteProductList({ products, loading = false }: Props) {
  const selectedProductId = useMapStore((s) => s.selectedProductId);
  const setSelectedProductId = useMapStore((s) => s.setSelectedProductId);
  const setFitProductId = useMapStore((s) => s.setFitProductId);
  const addItem = useBasket((s) => s.addItem);
  const basketItems = useBasket((s) => s.items);
  const requestedPeriod = usePeriodStore((s) => s.requestedPeriodMonths);

  function handleRowClick(product: RouteProduct) {
    setSelectedProductId(product.productId);
  }

  function handleAddToBasket(e: React.MouseEvent, product: RouteProduct) {
    e.stopPropagation();
    if (!hasValidPricing(product.prices)) return;
    const effectivePeriod = resolveEffectivePeriod(product.prices, requestedPeriod);
    const effectivePrice = resolveEffectivePrice(product.prices, requestedPeriod);
    addItem({
      productId: product.productId,
      productType: product.productType,
      name: product.name,
      title: product.title,
      coverage: product.coverage,
      licensePeriodMonths: effectivePeriod,
      priceAmount: effectivePrice?.priceAmount,
      currencyCode: effectivePrice?.currencyCode,
      availablePrices: product.prices,
      source: 'route',
      geometry: product.geometry,
    });
  }

  return (
    <div data-testid="route-product-list">
      <List disablePadding>
        {loading ? (
          Array.from({ length: 5 }, (_, i) => (
            <Box key={i} sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="40%" />
            </Box>
          ))
        ) : products.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
            No products on this route
          </Typography>
        ) : (
          products.map((product) => (
            <ProductCard
              key={product.productId}
              product={product}
              selected={selectedProductId === product.productId}
              inBasket={basketItems.some((i) => i.productId === product.productId)}
              onClick={() => handleRowClick(product)}
              onDoubleClick={() => setFitProductId(product.productId)}
              onAddToBasket={(e) => handleAddToBasket(e, product)}
            />
          ))
        )}
      </List>
    </div>
  );
}

interface ProductCardProps {
  product: RouteProduct;
  selected: boolean;
  inBasket: boolean;
  onClick: () => void;
  onDoubleClick?: () => void;
  onAddToBasket: (e: React.MouseEvent) => void;
}

function ProductCard({
  product,
  selected,
  inBasket,
  onClick,
  onDoubleClick,
  onAddToBasket,
}: ProductCardProps) {
  const requestedPeriod = usePeriodStore((s) => s.requestedPeriodMonths);
  const validPricing = hasValidPricing(product.prices);
  const effectivePrice = validPricing
    ? resolveEffectivePrice(product.prices, requestedPeriod)
    : undefined;

  return (
    <ListItemButton
      selected={selected}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      sx={{
        alignItems: 'flex-start',
        gap: 1,
        py: 1,
        px: 2,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {product.holdingStatus !== 'none' && (
            <Tooltip title={holdingLabel[product.holdingStatus]}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: holdingDotColor[product.holdingStatus],
                  flexShrink: 0,
                }}
              />
            </Tooltip>
          )}
          <Box
            sx={{
              px: 0.5,
              py: 0.125,
              borderRadius: 0.5,
              bgcolor: productTypeColor[product.productType],
              color: 'common.white',
              fontSize: '0.6rem',
              fontWeight: 700,
              lineHeight: 1.2,
              flexShrink: 0,
            }}
          >
            {productTypeLabel[product.productType]}
          </Box>
          {product.isRequired && (
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
              Required
            </Typography>
          )}
          <Typography variant="h6" noWrap sx={{ fontSize: '1rem', lineHeight: 1.4 }}>
            {product.name}
          </Typography>
        </Box>
        {product.title && (
          <Typography variant="caption" color="text.secondary" noWrap component="div">
            {product.title}
          </Typography>
        )}
        {product.productType === 'AVCS' && (coverageLabel(product.coverage) || effectivePrice) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {coverageLabel(product.coverage) && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                {coverageLabel(product.coverage)}
              </Typography>
            )}
            {effectivePrice && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                {effectivePrice.periodMonths} months
              </Typography>
            )}
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 0.25,
          pt: 0.25,
        }}
      >
        {product.holdingStatus === 'active' ? null : inBasket ? (
          <Typography variant="caption" color="text.secondary">
            In Basket
          </Typography>
        ) : validPricing ? (
          <Tooltip title="Add to order">
            <IconButton size="small" onClick={onAddToBasket} color="primary">
              <AddShoppingCartIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Pricing unavailable">
            <WarningAmberIcon fontSize="small" color="warning" />
          </Tooltip>
        )}
        {effectivePrice && (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ fontWeight: 600 }}>
            {currencySymbol(effectivePrice.currencyCode)}
            {effectivePrice.priceAmount.toFixed(2)}
          </Typography>
        )}
      </Box>
    </ListItemButton>
  );
}
